var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { differenceInDays, differenceInMonths, differenceInWeeks } from "date-fns";
import { isErr } from "healthex-common/result";
import { pluralize } from "healthex-common/utils";
import { t } from "i18next";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
import { BlockchainSigningType } from "healthex-common/blockchain.dtos";
export class AccountSettingsLoginAndSecurityViewModel {
    constructor(container) {
        this.formModel = new FormModel({
            currentPassword: "",
            newPassword: "",
            reEnteredNewPassword: "",
        }, ["currentPassword", "newPassword", "reEnteredNewPassword"]);
        this.editingPassword = false;
        this.authManager = container.authManager;
        this.organizationsManager = container.organizationsManager;
        this.bannerUIManager = container.bannerUIManager;
        this.passwordValidator = container.passwordValidator;
    }
    get myOrganization() {
        return this.organizationsManager.myOrganization.value;
    }
    get organizationPasswordConfiguration() {
        var _a;
        if (this.myOrganization === NOT_LOADED)
            return null;
        return (_a = this.myOrganization) === null || _a === void 0 ? void 0 : _a.passwordConfiguration;
    }
    get passwordSectionText() {
        if (this.authManager.passwordUpdatedAt === null)
            return null;
        const passwordUpdatedAt = new Date(this.authManager.passwordUpdatedAt);
        const daysSinceLastUpdate = Math.max(0, differenceInDays(new Date(), passwordUpdatedAt));
        if (daysSinceLastUpdate === 0) {
            return `Last updated less than a day ago`;
        }
        else if (daysSinceLastUpdate <= 6) {
            return `Last updated ${daysSinceLastUpdate} ${pluralize(daysSinceLastUpdate, "day", "days")} ago`;
        }
        else if (daysSinceLastUpdate <= 27) {
            const weeksSinceLastUpdate = Math.max(1, differenceInWeeks(new Date(), passwordUpdatedAt));
            return `Last updated ${weeksSinceLastUpdate} ${pluralize(weeksSinceLastUpdate, "week", "weeks")} ago`;
        }
        const monthsSinceLastUpdate = Math.max(1, differenceInMonths(new Date(), passwordUpdatedAt));
        return `Last updated ${monthsSinceLastUpdate} ${pluralize(monthsSinceLastUpdate, "month", "months")} ago`;
    }
    get showPasswordSection() {
        if (this.myOrganization === NOT_LOADED || this.myOrganization === null)
            return false;
        return !this.myOrganization.ssoEnabled;
    }
    get showAuthenticatorAppSection() {
        if (this.myOrganization === NOT_LOADED || this.myOrganization === null)
            return false;
        return this.myOrganization.blockchainSigningType === BlockchainSigningType.SIGNING_APP;
    }
    get authenticatorAppConnected() {
        if (this.authManager.onChainAddress) {
            return this.authManager.onChainAddress.length > 0;
        }
        return false;
    }
    get authenticatorAppSectionText() {
        return this.authenticatorAppConnected ? this.authManager.onChainAddress : "Register authenticator app";
    }
    toggleEditingPassword() {
        this.editingPassword = !this.editingPassword;
        if (!this.editingPassword) {
            this.clearPasswordInput();
        }
    }
    clearPasswordInput() {
        this.formModel.input = { currentPassword: "", newPassword: "", reEnteredNewPassword: "" };
    }
    async copyAuthenticatorAddressToClipboard() {
        var _a;
        await navigator.clipboard.writeText((_a = this.authenticatorAppSectionText) !== null && _a !== void 0 ? _a : "");
        this.bannerUIManager.displayBanner({ text: "Address copied to clipboard" });
    }
    validateInput() {
        const { notBlank } = validators;
        const f = this.formModel;
        f.clearErrors();
        const validationResults = this.passwordValidator.validateMultipleErrors({
            currentPassword: this.formModel.input.currentPassword,
            newPassword: this.formModel.input.newPassword,
            reEnteredNewPassword: this.formModel.input.reEnteredNewPassword || null,
            newPasswordHashed: null,
            currentPasswordHashed: null,
            passwordConfiguration: this.organizationPasswordConfiguration,
        });
        f.validateField("currentPassword", notBlank, "Enter your current password.");
        f.validateField("newPassword", notBlank, "Enter your new password.");
        f.validateField("reEnteredNewPassword", notBlank, "Enter your new password confirmation.");
        if (validationResults.includes("OLD_PASSWORD_REUSED")) {
            f.addError("newPassword", "Cannot reuse your current password.");
        }
        if (validationResults.includes("PASSWORD_CRITERIA_MISMATCH")) {
            f.addError("newPassword", "Password doesn't meet requirements. Please try again.");
        }
        if (validationResults.includes("PASSWORD_MISMATCH")) {
            f.addError("reEnteredNewPassword", "Passwords don't match. Please try again.");
        }
        return !f.hasErrors();
    }
    async savePassword() {
        this.validateInput();
        const result = await this.authManager.changePassword(this.formModel.input.newPassword, this.formModel.input.currentPassword);
        if (isErr(result)) {
            switch (result.error) {
                case "INCORRECT_CREDENTIALS":
                    this.formModel.addError("currentPassword", "Password is incorrect. Please try again.");
                    break;
                case "ACCOUNT_LOCKED":
                    this.formModel.addFormError(t("Your account is temporarily locked to prevent unauthorized use. You can either reset your password or try again later. If you still have trouble, contact your administrator."));
                    break;
                default:
                    break;
            }
        }
        else {
            await this.authManager.refreshTokenInfo();
            this.editingPassword = false;
            this.clearPasswordInput();
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AccountSettingsLoginAndSecurityViewModel.prototype, "editingPassword", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "myOrganization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "organizationPasswordConfiguration", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "passwordSectionText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "showPasswordSection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "showAuthenticatorAppSection", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "authenticatorAppConnected", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AccountSettingsLoginAndSecurityViewModel.prototype, "authenticatorAppSectionText", null);
